import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { BrandingProvider } from './context/BrandingContext';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import { X } from 'lucide-react';
import { P } from "@/config/permissionCodes";
// Pages
import LoginPage from './pages/Auth/LoginPage';
import RoleSelectionPage from './pages/Auth/RoleSelectionPage';
import TableSelectionPage from './pages/POS/TableSelectionPage';
import POSScreen from './pages/POS/POSScreen';
import BrowsePOSScreen from './pages/POS/BrowsePOSScreen';
import POSLandingPage from './pages/POS/POSLandingPage';
import AdminDashboard from './pages/Admin/AdminDashboard';
import MenuManagement from './pages/Admin/MenuManagement';
import ProtectedRoute from './components/shared/ProtectedRoute';
import ReportsLandingRedirect from './components/shared/ReportsLandingRedirect';
import TableManagement from './pages/Admin/TableManagement';
import BillSummaryTable from './pages/Admin/BillSummaryTable';
import DayClosePage from './pages/Admin/DayClosePage';
import CustomerPage from './pages/Admin/CustomerManagement';
import EmployeeManagementPage from './pages/Admin/EmployeeManagement';
import RoleManagementPage from './pages/Admin/RoleManagementPage';
import EmployeeAddEdit from './components/Admin/Employee-Management/employeeAdd-edit';
import EmployeeViewPage from './components/Admin/Employee-Management/EmployeeViewPage';
import AdminLayout from './components/Admin/Layout/AdminLayout';
import ModuleLayout from './layouts/ModuleLayout';
// Settings Pages (New Hub Structure)
import SettingsHubPage from './pages/Admin/Settings/SettingsHubPage';
import BranchSettingsPage from './pages/Admin/Settings/BranchSettingsPage';
import PaymentAccountsPage from './pages/Admin/Settings/PaymentAccountsPage';
import PrinterConfigPage from './pages/Admin/Settings/PrinterConfigPage';
import ReceiptConfigPage from './pages/Admin/Settings/ReceiptConfigPage';
import NotificationManagementPage from './pages/Admin/Settings/NotificationManagement';
import IntegrationManagementPage from './pages/Admin/Settings/IntegrationManagement';
import TaxSettingPage from './pages/Admin/Settings/TaxManagement';
import ResetPassword from './components/Admin/Settings/ResetPassword/resetPassword';
// Inventory Pages
import InventoryHubPage from './pages/Admin/Inventory/InventoryHubPage';

// Promotions Pages
import DiscountsPage from './pages/Admin/Promotions/DiscountsPage';
import LoyaltyProgramPage from './pages/Admin/Promotions/LoyaltyProgramPage';

// CRM Pages
import CampaignsPage from './pages/CRM/CampaignsPage';
import PublicFeedbackPage from './pages/CRM/CustomerFeedbackPage';
import FeedbackManagementPage from './pages/Admin/FeedbackManagement';

// Reports Pages
import ReportsHubPage from './pages/Admin/Reports/ReportsHubPage';
import DailySalesReport from './pages/Admin/Reports/DailySalesReport';
import ItemWiseSalesReport from './pages/Admin/Reports/ItemWiseSalesReport';
import PaymentAnalysisReport from './pages/Admin/Reports/PaymentAnalysisReport';
import GSTReport from './pages/Admin/Reports/GSTReport';
import HourlySalesReport from './pages/Admin/Reports/HourlySalesReport';
import DiscountAnalysisReport from './pages/Admin/Reports/DiscountAnalysisReport';
import CustomerInsightsReport from './pages/Admin/Reports/CustomerInsightsReport';
import LoyaltyPointsReport from './pages/Admin/Reports/LoyaltyPointsReport';
import WaiterPerformanceReport from './pages/Admin/Reports/WaiterPerformanceReport';
import TableUtilizationReport from './pages/Admin/Reports/TableUtilizationReport';
import KOTTimingReport from './pages/Admin/Reports/KOTTimingReport';
import StockStatusReport from './pages/Admin/Reports/StockStatusReport';
import ConsumptionReport from './pages/Admin/Reports/ConsumptionReport';
import CategorySalesReport from './pages/Admin/Reports/CategorySalesReport';
import RevenueSummaryReport from './pages/Admin/Reports/RevenueSummaryReport';
import NewVsRepeatReport from './pages/Admin/Reports/NewVsRepeatReport';

// AI Insights
import AIInsightsPage from './pages/Admin/AIInsights/AIInsightsPage';

// Inventory Module Pages
import InventoryDashboard from './pages/Inventory/InventoryDashboard';
import InventoryItemsPage from './pages/Inventory/InventoryItemsPage';
import RecipesPage from './pages/Inventory/RecipesPage';
import InventoryItemHistoryPage from './pages/Inventory/InventoryItemHistoryPage';
import UoMPage from './pages/Inventory/UoMPage';
import PurchaseOrdersPage from './pages/Inventory/PurchaseOrdersPage';
import SuppliersPage from './pages/Inventory/SuppliersPage';
import StockAdjustmentsPage from './pages/Inventory/StockAdjustmentsPage';
import WasteTrackingPage from './pages/Inventory/WasteTrackingPage';

// Coming Soon Page
import ComingSoon from './pages/ComingSoon';

// HRM Module Pages
import PayrollManagementPage from './pages/HRM/PayrollManagementPage';
import AttendanceManagementPage from './pages/HRM/AttendanceManagementPage';
import ShiftManagementPage from './pages/HRM/ShiftManagementPage';

// KDS (Kitchen Display System)
import KDSScreen from './pages/KDS/KDSScreen';

// Integrations Module
import IntegrationsPage from './pages/Integrations/IntegrationsPage';
import WhatsAppSettingsPage from './pages/Integrations/WhatsApp/WhatsAppSettingsPage';

// Captain App Module
import CaptainAppPage from './pages/CaptainApp/CaptainAppPage';

// QR Menu Module
import QRSettingsPage from './pages/QRMenu/QRSettingsPage';
import QRCodesPage from './pages/QRMenu/QRCodesPage';
import QROrdersPage from './pages/QRMenu/QROrdersPage';
import CustomerMenuPage from './pages/QRMenu/CustomerMenuPage';

// Shift Management
import { ShiftProvider } from './context/ShiftContext';

// Super Admin Module
import SuperAdminLogin from './pages/SuperAdmin/SuperAdminLogin';
// import OrganizationRegistry from './pages/SuperAdmin/OrganizationRegistry';
import Organizations from './pages/SuperAdmin/Organizations';
import TenantOnboarding from './pages/SuperAdmin/TenantOnboarding';
import BranchDetails from './pages/SuperAdmin/Branchdetails';
// import UserManagement from './pages/SuperAdmin/UserManagement';

// import RoleManagement from './components/Admin/HRM/RoleManagement';

// Root redirect component
function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent mb-4" />
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // If authenticated, go to POS, otherwise go to login
  return isAuthenticated() ? <Navigate to="/pos" replace /> : <Navigate to="/login" replace />;
}

function App() {
  return (
    <>
      {/* Enhanced Toast Configuration with Close Button */}
      <Toaster
        position="top-right"
        toastOptions={{
          // Default options for all toasts
          duration: 3000,
          style: {
            background: '#fff',
            color: '#1f2937',
            padding: '12px 16px',
            borderRadius: '10px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            fontWeight: '500',
            fontSize: '14px',
            maxWidth: '400px',
          },
          // Success toast styling - Teal theme
          success: {
            duration: 2500,
            iconTheme: {
              primary: '#14b8a6',
              secondary: '#fff',
            },
            style: {
              background: '#f0fdfa',
              color: '#115e59',
              border: '1px solid #14b8a6',
            },
          },
          // Error toast styling
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
            style: {
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #ef4444',
            },
          },
          // Loading toast styling
          loading: {
            iconTheme: {
              primary: '#3b82f6',
              secondary: '#fff',
            },
            style: {
              background: '#eff6ff',
              color: '#1e40af',
              border: '1px solid #3b82f6',
            },
          },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <div className="flex items-center gap-2 w-full">
                {icon}
                <span className="flex-1">{message}</span>
                {t.type !== 'loading' && (
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="p-1 rounded hover:bg-black/10 transition-colors flex-shrink-0"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>

      <AuthProvider>
        <BrandingProvider>
        <CartProvider>
          <Router>
            <Routes>
              {/* Root - Redirects based on auth status */}
              <Route path="/" element={<RootRedirect />} />

              {/* Auth Routes - Public */}
              <Route path="/login" element={<LoginPage />} />

              {/* Platform Admin Routes - Public (Uses X-Tenant-Setup-Key for auth) */}
              {/* Platform Admin Routes - Public (Uses X-Tenant-Setup-Key for auth) */}
              <Route path="/platform/login" element={<SuperAdminLogin />} />
              <Route path="/platform/organizations" element={<Organizations />} />

              <Route path="/platform/branch/:branchId" element={<BranchDetails />} />
              <Route path="/platform/tenant-setup" element={<TenantOnboarding />} />
              {/* <Route path="/platform/users" element={<UserManagement />} /> */}

              {/* Role Selection - Protected */}
              <Route
                path="/select-role"
                element={
                  <ProtectedRoute>
                    <RoleSelectionPage />
                  </ProtectedRoute>
                }
              />

              {/* KDS Route - Standalone screen without navigation */}
              <Route
                path="/kds"
                element={
                  <ProtectedRoute>
                    <KDSScreen />
                  </ProtectedRoute>
                }
              />

              {/* POS Routes - Protected, wrapped with ShiftProvider */}
              {/* POS Landing - Service Mode Selector */}
              <Route
                path="/pos"
                element={
                  <ProtectedRoute requiredRole="pos">
                    <ShiftProvider>
                      <POSLandingPage />
                    </ShiftProvider>
                  </ProtectedRoute>
                }
              />
              {/* Dine-In Mode - Table Selection */}
              <Route
                path="/pos/tables"
                element={
                  <ProtectedRoute requiredRole="pos">
                    <ShiftProvider>
                      <TableSelectionPage />
                    </ShiftProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pos/browse"
                element={
                  <ProtectedRoute requiredRole="pos">
                    <ShiftProvider>
                      <BrowsePOSScreen />
                    </ShiftProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pos/billing/:tableId"
                element={
                  <ProtectedRoute requiredRole="pos">
                    <ShiftProvider>
                      <POSScreen />
                    </ShiftProvider>
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes - Protected */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customers"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <CustomerPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/employee"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <EmployeeManagementPage />
                  </ProtectedRoute>
                }
              />


              <Route
                path="/admin/employee/add"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <EmployeeAddEdit />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />


              <Route
                path="/admin/employee/edit/:id"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <EmployeeAddEdit />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />


              <Route
                path="/admin/employee/view/:id"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <EmployeeViewPage />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bills-summary"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <BillSummaryTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/day-close"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <DayClosePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/menu"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <MenuManagement />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/tables" element={<TableManagement />} />

              {/* Inventory Hub */}
              <Route
                path="/admin/inventory"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <InventoryHubPage />
                  </ProtectedRoute>
                }
              />

              {/* Promotions Pages - Redirects to CRM for backward compatibility */}
              <Route
                path="/admin/promotions/discounts"
                element={<Navigate to="/crm/discounts" replace />}
              />
              <Route
                path="/admin/promotions/loyalty"
                element={<Navigate to="/crm/loyalty" replace />}
              />

              {/* Settings Hub and Sub-pages */}
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <SettingsHubPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/settings/branch"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <BranchSettingsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/settings/payment-accounts"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <PaymentAccountsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/settings/printers"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <PrinterConfigPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/settings/receipts"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <ReceiptConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings/receipts"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <ReceiptConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings/notifications"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <NotificationManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings/integrations"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <IntegrationManagementPage />
                  </ProtectedRoute>
                }
              />


            <Route
              path="/profile/reset-password"
              element={
                <ProtectedRoute>
                  <ResetPassword />
                </ProtectedRoute>
              }
            />

              {/* Reports Hub and Sub-pages */}
              <Route
                path="/admin/reports/*"
                element={
                  <ProtectedRoute requiredPermission="reports">
                    <ReportsHubPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/daily-sales"
                element={
                  <ProtectedRoute requiredPermission="reports">
                    <DailySalesReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/item-wise-sales"
                element={
                  <ProtectedRoute requiredPermission="reports">
                    <ItemWiseSalesReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/payment-analysis"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <PaymentAnalysisReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/gst-report"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <GSTReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/hourly-sales"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <HourlySalesReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/discount-analysis"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <DiscountAnalysisReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/customer-insights"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <CustomerInsightsReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/loyalty-report"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <LoyaltyPointsReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/waiter-performance"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <WaiterPerformanceReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/table-utilization"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <TableUtilizationReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/kot-timing"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <KOTTimingReport />
                  </ProtectedRoute>
                }
              />
             <Route
              path="/admin/reports/consumption-report"
              element={
                <ProtectedRoute requiredPermission="reports">
                  <ConsumptionReport />
                </ProtectedRoute>
              }
            />
              <Route
                path="/admin/reports/category-sales"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <CategorySalesReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/revenue-summary"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <RevenueSummaryReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports/new-vs-repeat"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <NewVsRepeatReport />
                  </ProtectedRoute>
                }
              />

              {/* AI Insights */}
              <Route
                path="/admin/ai-insights"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AIInsightsPage />
                  </ProtectedRoute>
                }
              />

              {/* ============================================ */}
              {/* NEW MODULE ROUTES (Modular Architecture)     */}
              {/* ============================================ */}

              {/* Finance Module - Smart redirect to first accessible report */}
              <Route
                path="/finance"
                element={
                  <ProtectedRoute requiredPermission="reports">
                    <ReportsLandingRedirect />
                  </ProtectedRoute>
                }
              />
              {/* Sales Reports */}
              <Route
                path="/finance/reports/daily-sales"
                element={
                  <ProtectedRoute requiredPermission="reports.sales">
                    <DailySalesReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/item-wise-sales"
                element={
                  <ProtectedRoute requiredPermission="reports.sales">
                    <ItemWiseSalesReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/category-sales"
                element={
                  <ProtectedRoute requiredPermission="reports.sales">
                    <CategorySalesReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/hourly-sales"
                element={
                  <ProtectedRoute requiredPermission="reports.sales">
                    <HourlySalesReport />
                  </ProtectedRoute>
                }
              />
              {/* Financial Reports */}
              <Route
                path="/finance/reports/payment-analysis"
                element={
                  <ProtectedRoute requiredPermission="reports.financial">
                    <PaymentAnalysisReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/gst-report"
                element={
                  <ProtectedRoute requiredPermission="reports.financial">
                    <GSTReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/discount-analysis"
                element={
                  <ProtectedRoute requiredPermission="reports.financial">
                    <DiscountAnalysisReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/revenue-summary"
                element={
                  <ProtectedRoute requiredPermission="reports.financial">
                    <RevenueSummaryReport />
                  </ProtectedRoute>
                }
              />
              {/* Customer Reports */}
              <Route
                path="/finance/reports/customer-insights"
                element={
                  <ProtectedRoute requiredPermission="reports.customers">
                    <CustomerInsightsReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/loyalty-report"
                element={
                  <ProtectedRoute requiredPermission="reports.customers">
                    <LoyaltyPointsReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/new-vs-repeat"
                element={
                  <ProtectedRoute requiredPermission="reports.customers">
                    <NewVsRepeatReport />
                  </ProtectedRoute>
                }
              />
              {/* Operations Reports */}
              <Route
                path="/finance/reports/waiter-performance"
                element={
                  <ProtectedRoute requiredPermission="reports.operations">
                    <WaiterPerformanceReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/table-utilization"
                element={
                  <ProtectedRoute requiredPermission="reports.operations">
                    <TableUtilizationReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/kot-timing"
                element={
                  <ProtectedRoute requiredPermission="reports.operations">
                    <KOTTimingReport />
                  </ProtectedRoute>
                }
              />
              {/* Inventory Reports */}
              <Route
                path="/finance/reports/stock-status"
                element={
                  <ProtectedRoute requiredPermission="reports.inventory">
                    <StockStatusReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/reports/consumption-report"
                element={
                  <ProtectedRoute requiredPermission="reports.inventory">
                    <ConsumptionReport />
                  </ProtectedRoute>
                }
              />


           <Route
                path="/backoffice"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <Navigate to="/backoffice/dashboard" replace />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/backoffice/dashboard"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <InventoryDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/backoffice/roles"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <RoleManagementPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/backoffice/users"
                element={
                  <ProtectedRoute requiredPermission="back_office">
                    <CustomerPage />
                  </ProtectedRoute>
                }
              />
              {/* HRM Module - Redirect to employees */}
              <Route
                path="/hrm"
                element={
                  <ProtectedRoute requiredPermission="hrm">
                    <Navigate to="/hrm/employees" replace />
                  </ProtectedRoute>
                }
              />
              <Route path="/hrm/employees"
                  element={
                    <ProtectedRoute requiredPermission="hrm">
                      <EmployeeManagementPage />
                    </ProtectedRoute>
                  }
                />
              <Route
                path="/hrm/employees/add"
                element={
                  <ProtectedRoute requiredPermission="hrm">
                    <ModuleLayout>
                      <EmployeeAddEdit />
                    </ModuleLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrm/employees/edit/:id"
                element={
                  <ProtectedRoute requiredPermission="hrm">
                    <ModuleLayout>
                      <EmployeeAddEdit />
                    </ModuleLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrm/employees/view/:id"
                element={
                  <ProtectedRoute requiredPermission="hrm">
                    <ModuleLayout>
                      <EmployeeViewPage />
                    </ModuleLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrm/reports/waiter-performance"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <WaiterPerformanceReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrm/reports/kot-timing"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <KOTTimingReport />
                  </ProtectedRoute>
                }
              />
              {/* HRM Pages */}
              <Route
                path="/hrm/attendance"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AttendanceManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrm/payroll"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <PayrollManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrm/shifts"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ShiftManagementPage />
                  </ProtectedRoute>
                }

              />
              <Route
              path="/hrm/role-permissions"
              element={
                <ProtectedRoute requiredRole="admin">
                    <RoleManagementPage />
                </ProtectedRoute>
              }
            />

              {/* CRM Module - Redirect to customers */}
             <Route
                path="/crm"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <Navigate to="/crm/customers" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/customers"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CustomerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/campaigns"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CampaignsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/discounts"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <DiscountsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/loyalty"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <LoyaltyProgramPage />
                  </ProtectedRoute>
                }
              />
              <Route
              path="/crm/reports/customer-insights"
              element={
                <ProtectedRoute requiredPermission="crm">
                  <CustomerInsightsReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/crm/reports/loyalty-report"
              element={
                <ProtectedRoute requiredPermission="crm">
                  <LoyaltyPointsReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/crm/reports/new-vs-repeat"
              element={
                <ProtectedRoute requiredPermission="crm">
                  <NewVsRepeatReport />
                </ProtectedRoute>
              }
            />
            <Route path="/crm/feedback" element={<FeedbackManagementPage />} />

              {/* Inventory Module */}
              <Route
                path="/inventory/*"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <InventoryDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/items"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <InventoryItemsPage />
                  </ProtectedRoute>
                }
              />
              {/* Inventory Item History — replaces the old Item History modal */}
              <Route
                path="/inventory/items/:id/history"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <InventoryItemHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/recipes"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <RecipesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/uom"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <UoMPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/adjustments"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <StockAdjustmentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/reports/stock-status"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <StockStatusReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/reports/consumption"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ConsumptionReport />
                  </ProtectedRoute>
                }
              />
              {/* Inventory Coming Soon Pages */}
              <Route
                path="/inventory/purchase-orders"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <PurchaseOrdersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/suppliers"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <SuppliersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/wastage"
                element={
                  <ProtectedRoute requiredPermission="inventory">
                    <WasteTrackingPage />
                  </ProtectedRoute>
                }
              />

              {/* AI Insights Module */}
              <Route
                path="/ai-insights"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AIInsightsPage />
                  </ProtectedRoute>
                }
              />
              {/* AI Insights Coming Soon Pages */}
              <Route
                path="/ai-insights/forecasting"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ComingSoon />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-insights/recommendations"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ComingSoon />
                  </ProtectedRoute>
                }
              />

              {/* Integrations Module */}
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <IntegrationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/whatsapp"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <WhatsAppSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* Captain App Module */}
              <Route
                path="/captain-app"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <CaptainAppPage />
                  </ProtectedRoute>
                }
              />

              {/* QR Menu Module */}
              <Route
                path="/qr-menu"
                element={
                  <ProtectedRoute requiredPermission="qr_menu">
                    <Navigate to="/qr-menu/settings" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qr-menu/settings"
                element={
                  <ProtectedRoute requiredPermission="qr_menu">
                    <QRSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qr-menu/orders"
                element={
                  <ProtectedRoute requiredPermission="qr_menu">
                    <QROrdersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qr-menu/qr-codes"
                element={
                  <ProtectedRoute requiredPermission="qr_menu">
                    <QRCodesPage />
                  </ProtectedRoute>
                }
              />

              {/* Public QR Menu - Customer facing (no auth) */}
              <Route path="/qr/:branchId/:token" element={<CustomerMenuPage />} />

                {/* Public Feedback Form - customer facing */}
              <Route path="/feedback/:branchId" element={<PublicFeedbackPage />} />


              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </CartProvider>
        </BrandingProvider>
      </AuthProvider>
    </>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-orange-50 to-amber-50">
      <div className="text-center max-w-md mx-auto px-6">
        {/* 404 Icon */}
        <div className="mb-6 relative">
          <div className="text-9xl font-black text-slate-200 select-none">404</div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-24 h-24 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        {/* Error Message */}
        <h1 className="text-3xl font-bold text-gray-900 mb-3">Page Not Found</h1>
        <p className="text-gray-600 mb-8 text-lg">
          Oops! The page you're looking for doesn't exist or has been moved.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/pos"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Go to POS
          </a>

          <a
            href="/admin/dashboard"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-all duration-200 border-2 border-slate-200 hover:border-slate-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            Go to Dashboard
          </a>
        </div>

        {/* Help Text */}
        <p className="mt-8 text-sm text-slate-500">
          Need help? Contact support or go back to the home page.
        </p>
      </div>
    </div>
  );
}

export default App;